import React from 'react';
import { priceFormatter } from '@noths/polaris-client-utils';

import { QUANTITY_COPY } from 'src/components/atoms/Quantity/copy';
import {
  ADDED_COPY,
  ITEM_PRICE_COPY,
  OPTIONS_COPY,
  PERSONALISATION_COPY,
  REMOVED_COPY,
  TOTAL_COPY,
} from 'src/components/molecules/PriceBreakdown/constants/copy';
import {
  PRICE_BREAKDOWN_ADDED_TESTID,
  PRICE_BREAKDOWN_QUANTITY_TESTID,
  PRICE_BREAKDOWN_REMOVED_TESTID,
  PRICE_BREAKDOWN_TESTID,
} from 'src/components/molecules/PriceBreakdown/constants/testIds';
import type { Price } from 'src/types/price';
import * as styles from './PriceBreakdown.styles';

export interface OptionsBreakdown {
  amountAdded: number;
  amountRemoved: number;
}

export interface PriceBreakdownStateProps {
  optionsTotal: number;
  personalisable: boolean;
  priceBreakdown: OptionsBreakdown;
  productPrice: Price;
  quantity: number;
  totalPrice: number;
}

type PriceBreakdownProps = PriceBreakdownStateProps;

const AddedAndRemovedAmounts = ({
  priceBreakdown,
  productPrice,
}: Pick<PriceBreakdownProps, 'priceBreakdown' | 'productPrice'>) => (
  <>
    {priceBreakdown.amountAdded > 0 && (
      <>
        <p css={styles.optionAmountLabel}>{ADDED_COPY}</p>
        <p css={styles.priceAmount} data-testid={PRICE_BREAKDOWN_ADDED_TESTID}>
          <span css={styles.priceAmountPlusSymbol}>+</span>
          {priceFormatter(productPrice.currency, priceBreakdown.amountAdded)}
        </p>
      </>
    )}
    {priceBreakdown.amountRemoved < 0 && (
      <>
        <p css={styles.optionAmountLabel}>{REMOVED_COPY}</p>
        <p css={styles.priceAmount} data-testid={PRICE_BREAKDOWN_REMOVED_TESTID}>
          <span css={styles.priceAmountMinusSymbol}>&minus;</span>
          {priceFormatter(productPrice.currency, priceBreakdown.amountRemoved).replace('-', '')}
        </p>
      </>
    )}
  </>
);

export const PriceBreakdown = ({
  optionsTotal,
  personalisable,
  priceBreakdown,
  productPrice,
  quantity,
  totalPrice,
}: PriceBreakdownProps) => {
  const headingText = personalisable ? PERSONALISATION_COPY : OPTIONS_COPY;
  const multipleItems = quantity > 1;
  // The rows above the total only earn their place when the total is more than the product
  // price: priced options, more than one item, or both. Otherwise they would restate it.
  const itemised = optionsTotal !== 0 || multipleItems;

  return (
    <div css={styles.priceBreakdown} data-testid={PRICE_BREAKDOWN_TESTID}>
      {itemised && (
        <>
          <p css={styles.priceLabel}>{ITEM_PRICE_COPY}</p>
          <p css={styles.priceAmount}>
            {priceFormatter(productPrice.currency, productPrice.amount)}
          </p>
          {optionsTotal !== 0 && (
            <div css={styles.optionsBreakdown}>
              <p css={styles.optionsBreakdownCopy}>{headingText}</p>
              <AddedAndRemovedAmounts priceBreakdown={priceBreakdown} productPrice={productPrice} />
            </div>
          )}
          {multipleItems && (
            <>
              <p css={styles.priceLabel}>{QUANTITY_COPY}</p>
              <p css={styles.priceAmount} data-testid={PRICE_BREAKDOWN_QUANTITY_TESTID}>
                &times;{quantity}
              </p>
            </>
          )}
        </>
      )}

      <p css={styles.totalLabel}>{TOTAL_COPY}</p>
      <p css={styles.totalPrice}>{priceFormatter(productPrice.currency, totalPrice)}</p>
    </div>
  );
};
